// src/services/firebase.js
// Firebase v10 modular SDK — DO NOT use legacy compat syntax

import { initializeApp } from "firebase/app";
import {
  getAuth,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  browserLocalPersistence,
  setPersistence,
} from "firebase/auth";
import {
  getFirestore,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  addDoc,
  deleteDoc,
  collection,
  query,
  where,
  orderBy,
  getDocs,
  serverTimestamp,
  limit,
} from "firebase/firestore";

// ─── Firebase config ────────────────────────────────────────────────────────
// Copy these values from your Firebase project console:
// Project Settings → Your apps → Web app → SDK setup & configuration
const firebaseConfig = {
  apiKey:            process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain:        process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId:         process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket:     process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId:             process.env.REACT_APP_FIREBASE_APP_ID,
};

const app  = initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const db   = getFirestore(app);

// Ensure auth session persists across browser sessions (localStorage-backed)
setPersistence(auth, browserLocalPersistence).catch(() => {});

// ─── Auth helpers ────────────────────────────────────────────────────────────

export const registerUser = async (email, password) => {
  try {
    const cred = await createUserWithEmailAndPassword(auth, email, password);
    return { user: cred.user, error: null };
  } catch (err) {
    return { user: null, error: err.message };
  }
};

export const loginUser = async (email, password) => {
  try {
    const cred = await signInWithEmailAndPassword(auth, email, password);
    return { user: cred.user, error: null };
  } catch (err) {
    return { user: null, error: err.message };
  }
};

export const logoutUser = async () => {
  try {
    await signOut(auth);
    return { error: null };
  } catch (err) {
    return { error: err.message };
  }
};

export const onAuthChange = (callback) => onAuthStateChanged(auth, callback);

// ─── UserProfile ─────────────────────────────────────────────────────────────

export const getUserProfile = async (uid) => {
  try {
    const snap = await getDoc(doc(db, "users", uid));
    return snap.exists() ? { data: snap.data(), error: null } : { data: null, error: null };
  } catch (err) {
    return { data: null, error: err.message };
  }
};

export const createUserProfile = async (uid, profile) => {
  try {
    await setDoc(doc(db, "users", uid), {
      ...profile,
      uid,
      created_at: serverTimestamp(),
    });
    return { error: null };
  } catch (err) {
    return { error: err.message };
  }
};

export const updateUserProfile = async (uid, updates) => {
  try {
    await updateDoc(doc(db, "users", uid), updates);
    return { error: null };
  } catch (err) {
    return { error: err.message };
  }
};

// ─── Weight Entries ──────────────────────────────────────────────────────────

export const addWeightEntry = async (uid, entry) => {
  try {
    const ref = await addDoc(collection(db, "users", uid, "weights"), {
      ...entry,
      uid,
      created_at: serverTimestamp(),
    });
    return { id: ref.id, error: null };
  } catch (err) {
    return { id: null, error: err.message };
  }
};

export const updateWeightEntry = async (uid, entryId, updates) => {
  try {
    await updateDoc(doc(db, "users", uid, "weights", entryId), updates);
    return { error: null };
  } catch (err) {
    return { error: err.message };
  }
};

export const deleteWeightEntry = async (uid, entryId) => {
  try {
    await deleteDoc(doc(db, "users", uid, "weights", entryId));
    return { error: null };
  } catch (err) {
    return { error: err.message };
  }
};

export const getWeightEntries = async (uid, limitCount = 90) => {
  try {
    const q = query(
      collection(db, "users", uid, "weights"),
      orderBy("date", "asc"),
      limit(limitCount)
    );
    const snap = await getDocs(q);
    const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    return { data, error: null };
  } catch (err) {
    return { data: [], error: err.message };
  }
};

export const getWeightEntryByDate = async (uid, date) => {
  try {
    const q = query(
      collection(db, "users", uid, "weights"),
      where("date", "==", date),
      limit(1)
    );
    const snap = await getDocs(q);
    if (snap.empty) return { data: null, error: null };
    const d = snap.docs[0];
    return { data: { id: d.id, ...d.data() }, error: null };
  } catch (err) {
    return { data: null, error: err.message };
  }
};

// ─── Food Entries ────────────────────────────────────────────────────────────

export const addFoodEntry = async (uid, entry) => {
  try {
    const ref = await addDoc(collection(db, "users", uid, "foods"), {
      ...entry,
      uid,
      created_at: serverTimestamp(),
    });
    return { id: ref.id, error: null };
  } catch (err) {
    return { id: null, error: err.message };
  }
};

export const deleteFoodEntry = async (uid, entryId) => {
  try {
    await deleteDoc(doc(db, "users", uid, "foods", entryId));
    return { error: null };
  } catch (err) {
    return { error: err.message };
  }
};

export const getFoodEntriesByDate = async (uid, date) => {
  try {
    const q = query(
      collection(db, "users", uid, "foods"),
      where("date", "==", date),
      orderBy("created_at", "asc")
    );
    const snap = await getDocs(q);
    const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    return { data, error: null };
  } catch (err) {
    return { data: [], error: err.message };
  }
};

export const getFoodEntriesRange = async (uid, startDate, endDate) => {
  try {
    const q = query(
      collection(db, "users", uid, "foods"),
      where("date", ">=", startDate),
      where("date", "<=", endDate),
      orderBy("date", "asc")
    );
    const snap = await getDocs(q);
    const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    return { data, error: null };
  } catch (err) {
    return { data: [], error: err.message };
  }
};

// ─── Exercise Entries ────────────────────────────────────────────────────────

export const addExerciseEntry = async (uid, entry) => {
  try {
    const ref = await addDoc(collection(db, "users", uid, "exercises"), {
      ...entry,
      uid,
      created_at: serverTimestamp(),
    });
    return { id: ref.id, error: null };
  } catch (err) {
    return { id: null, error: err.message };
  }
};

export const deleteExerciseEntry = async (uid, entryId) => {
  try {
    await deleteDoc(doc(db, "users", uid, "exercises", entryId));
    return { error: null };
  } catch (err) {
    return { error: err.message };
  }
};

export const getExerciseEntriesByDate = async (uid, date) => {
  try {
    const q = query(
      collection(db, "users", uid, "exercises"),
      where("date", "==", date),
      orderBy("created_at", "asc")
    );
    const snap = await getDocs(q);
    const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    return { data, error: null };
  } catch (err) {
    return { data: [], error: err.message };
  }
};

export const getExerciseEntriesRange = async (uid, startDate, endDate) => {
  try {
    const q = query(
      collection(db, "users", uid, "exercises"),
      where("date", ">=", startDate),
      where("date", "<=", endDate),
      orderBy("date", "asc")
    );
    const snap = await getDocs(q);
    const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    return { data, error: null };
  } catch (err) {
    return { data: [], error: err.message };
  }
};

// ─── Saved Meals ─────────────────────────────────────────────────────────────

export const addSavedMeal = async (uid, meal) => {
  try {
    const ref = await addDoc(collection(db, "users", uid, "savedMeals"), {
      ...meal,
      uid,
      created_at: serverTimestamp(),
    });
    return { id: ref.id, error: null };
  } catch (err) {
    return { id: null, error: err.message };
  }
};

export const deleteSavedMeal = async (uid, mealId) => {
  try {
    await deleteDoc(doc(db, "users", uid, "savedMeals", mealId));
    return { error: null };
  } catch (err) {
    return { error: err.message };
  }
};

export const getSavedMeals = async (uid) => {
  try {
    const q = query(
      collection(db, "users", uid, "savedMeals"),
      orderBy("created_at", "desc")
    );
    const snap = await getDocs(q);
    const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    return { data, error: null };
  } catch (err) {
    return { data: [], error: err.message };
  }
};

// ─── Streaks ─────────────────────────────────────────────────────────────────

export const getStreakData = async (uid) => {
  try {
    const snap = await getDoc(doc(db, "users", uid, "streaks", "data"));
    return snap.exists()
      ? { data: snap.data(), error: null }
      : { data: null, error: null };
  } catch (err) {
    return { data: null, error: err.message };
  }
};

export const setStreakData = async (uid, streakData) => {
  try {
    await setDoc(doc(db, "users", uid, "streaks", "data"), {
      ...streakData,
      uid,
    });
    return { error: null };
  } catch (err) {
    return { error: err.message };
  }
};

// ─── Adaptive Notifications ───────────────────────────────────────────────────

export const getAdaptiveNotifications = async (uid) => {
  try {
    const q = query(
      collection(db, "users", uid, "notifications"),
      orderBy("created_at", "desc"),
      limit(10)
    );
    const snap = await getDocs(q);
    const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    return { data, error: null };
  } catch (err) {
    return { data: [], error: err.message };
  }
};

export const addAdaptiveNotification = async (uid, notification) => {
  try {
    const ref = await addDoc(collection(db, "users", uid, "notifications"), {
      ...notification,
      uid,
      dismissed: false,
      created_at: serverTimestamp(),
    });
    return { id: ref.id, error: null };
  } catch (err) {
    return { id: null, error: err.message };
  }
};

export const dismissNotification = async (uid, notificationId) => {
  try {
    await updateDoc(doc(db, "users", uid, "notifications", notificationId), {
      dismissed: true,
    });
    return { error: null };
  } catch (err) {
    return { error: err.message };
  }
};
